import * as v from "valibot";
import { useEffect } from "react";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import type { CorasPageParamsByPage } from "@coras-io/embed";
import { getTextDirection } from "@coras-io/embed/helpers";
import { site } from "../config";
import { CorasPageView } from "../coras/CorasPageView";
import { LinkedTravelNotice } from "../coras/LinkedTravelNotice";
import { withStoredLocation } from "../coras/last-location";
import { corasUrl } from "../coras/url";

// Never throws: parse runs before beforeLoad. See /getting-started/routing.
const routeParamsSchema = v.object({
  locale: v.fallback(v.picklist(site.locales), site.locale),
  currency: v.fallback(v.picklist(site.currencies), site.currency),
});

export const Route = createFileRoute("/$locale/$currency")({
  component: LocaleCurrencyLayout,
  params: { parse: (rawParams) => v.parse(routeParamsSchema, rawParams) },
});

// An unknown first segment parses as a details id. See /getting-started/routing.
function resolvedHref(href: string, locale: string, currency: string): string {
  const url = new URL(href, window.location.origin);
  const rest = url.pathname.split("/").slice(3).join("/");
  url.pathname = `/${locale}/${currency}/${rest}`;
  return url.href;
}

function LocaleCurrencyLayout() {
  const { locale, currency } = Route.useParams();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = getTextDirection(locale);
  }, [locale]);

  const { page, params } = corasUrl.parse(
    resolvedHref(location.href, locale, currency),
  );

  const resolvedParams =
    page === "landing" ? withStoredLocation(params) : params;

  return (
    <>
      <CorasPageView
        page={page}
        params={resolvedParams as CorasPageParamsByPage[typeof page]}
        locale={locale}
        currency={currency}
      />
      <Outlet />
      <LinkedTravelNotice locale={locale} />
    </>
  );
}
